import { StreamShapes } from "@sablier/v2-constants";
import { Translate } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { useCreateGroupAccessor } from "~/client/hooks";
import type { StreamShape } from "@sablier/v2-constants";
import type { ITiming } from "@sablier/v2-types";
import type { ComponentType } from "react";
import type {
  IExtensionCheck,
  IExtensionDependencies,
  IExtensionOverrides,
  IExtensionParamsGroup,
  IExtensionParamsSingle,
  IExtensionResultGroup,
  IExtensionResultSingle,
  IPrecomputeResult,
  ISummaryExtension,
} from "~/client/types";

export const Extension = {
  LINEAR: StreamShapes.linear.id,
  CLIFF: StreamShapes.cliff.id,
  DYNAMIC_EXPONENTIAL: StreamShapes.dynamicExponential.id,
  DYNAMIC_CLIFF_EXPONENTIAL: StreamShapes.dynamicCliffExponential.id,
  DYNAMIC_STEPPER: StreamShapes.dynamicStepper.id,
  DYNAMIC_TIMELOCK: StreamShapes.dynamicTimelock.id,
  DYNAMIC_UNLOCK_LINEAR: StreamShapes.dynamicUnlockLinear.id,
  DYNAMIC_UNLOCK_CLIFF: StreamShapes.dynamicUnlockCliff.id,
};

export type IConfiguration = {
  check: (params: IExtensionCheck) => string | undefined;
  Form: {
    Group?: ComponentType<{ streamId: string }>;
    GroupGeneral?: ComponentType<unknown>;
    Single?: ComponentType<unknown>;
  };
  identify: (stream: Stream) => boolean;
  initial(params: IExtensionConstructorParams): IExtension;
  overrides(): IExtensionOverrides | undefined;
  process: {
    group: (params: IExtensionParamsGroup) => Promise<IExtensionResultGroup>;
    single: (params: IExtensionParamsSingle) => Promise<IExtensionResultSingle>;
  };
  shape: StreamShape | string;
  summary: (
    dependencies: IExtensionDependencies,
    extras: unknown,
    t: Translate,
  ) => ISummaryExtension | undefined;
  simulate: (params: IExtensionParamsSingle) => Stream;
  precompute: {
    single: (params: IExtensionParamsSingle) => IPrecomputeResult;
    group: (params: IExtensionParamsGroup) => IPrecomputeResult;
  };
};

export type Extension = (typeof Extension)[keyof typeof Extension];

export interface IExtension {
  purpose?: Extension;
}

export interface IExtensionConstructorParams {
  shape?: StreamShape | string;
  cliffDuration?: string;
  cliffEnd?: string;
  steps?: string;
  unlock?: string;
}

export interface ICheck {
  t: Translate;
  data: IExtension;
  isLoadingIncluded: boolean;
  isWarningIncluded: boolean;
  timing: ITiming;
}

export function setup<E = unknown>(
  access: ReturnType<typeof useCreateGroupAccessor<E>>,
  id: string,
) {
  const state = access();
  const update = state.api.updateFields;
  const streams = state.fields.streams.value;
  const stream = streams.find((s) => s.id === id)!;
  const index = streams.findIndex((s) => s.id == id);

  const isValid = !(_.isNil(stream) || index === -1);

  return {
    index,
    isValid,
    stream,
    streams,
    update,
  };
}
